import { ref } from 'vue';
import { defineStore } from 'pinia';
import useAxiosInstance from '@/composables/axiosInstance';
import useUISessionStore from './ui-session';
import AuthenticationIsTrustedDevice from '@/interfaces/AuthenticationIsTrustedDevice';
import AuthenticationTrustedDevice from '@/interfaces/AuthenticationTrustedDevice';
import useTFAuthenticationSettingsStore from './tf-authentication-settings';

enum ModifyEmployeeAction {
  Enable = 1,
  Reset,
  Disable,
}

interface State {
  trustedDevices : AuthenticationTrustedDevice[];
}

interface TrustedDeviceInformationForAdmin {
  'total' : number;
  'totalPages' : number;
  'currentPage' : number;
  'results' : AuthenticationTrustedDevice[];
}

const useTFAuthenticationDeviceStore = defineStore({
  id: 'authentication-devices',
  state: () => ref<State>({
    trustedDevices: [],
  }),
  actions: {
    async initStore() : Promise<void> {
      const uiSessionStore = useUISessionStore();
      if (uiSessionStore.isExpired) {
        await uiSessionStore.forceRefresh();
      }
      await this.fetchTrustedDevices();
    },
    async fetchTrustedDevices() : Promise<void> {
      try {
        const uiSessionStore = useUISessionStore();
        const { data } = await useAxiosInstance.get<TrustedDeviceInformationForAdmin>(`/TwoFactorAuthentication/GetTrustedDeviceInformationForEmployee?EmpCode=${uiSessionStore.user.empCode}`);
        this.trustedDevices = data.results;
      } catch (error : any) {
        console.error(error);
      }
    },
    async removeDevice(device : AuthenticationTrustedDevice) {
      const { user } = useUISessionStore();

      try {
        await useAxiosInstance.post('/TwoFactorAuthentication/RemoveTrustedDevice', {
          empCode: user.empCode,
          uniqueIdentifier: device.uniqueIdentifier,
        });
        this.trustedDevices = this.trustedDevices.filter((item) => item.uniqueIdentifier !== device.uniqueIdentifier);
      } catch (error : any) {
        console.error(error);
      }
    },
    async removeAllTrustedDevices() : Promise<void> {
      try {
        const { user } = useUISessionStore();
        const authenticationSettingStore = useTFAuthenticationSettingsStore();
        const isTrustedDevice : AuthenticationIsTrustedDevice = {
          isVerifiedEmployee: false,
          isDeviceExpired: true,
        };

        await useAxiosInstance.post('/TwoFactorAuthentication/RemoveTrustedDevicesByEmpCodes', {
          empCodes: [
            user.empCode,
          ],
        });

        await useAxiosInstance.post('/TwoFactorAuthentication/UpdateEmployeeAuthenticationStatuses', {
          action: ModifyEmployeeAction.Reset,
          modifiedByEmpCode: user.empCode,
          empCodes: [
            user.empCode,
          ],
        });

        authenticationSettingStore.setTrustedDevice(isTrustedDevice);
        this.trustedDevices = [];
      } catch (error : any) {
        console.error(error);
      }
    },
  },
});

export default useTFAuthenticationDeviceStore;
